'use client';

import React, { useEffect, useState } from 'react';
import { ProductInformationDocumentData } from 'prismicio-types';
import ProductAssistanceDrawerHeader from './product-assistance-drawer-header';
import ProductAssistanceDrawerContent from './product-assistance-drawer-content';
import {
    Drawer,
    DrawerFooter,
    DrawerHeader,
    DrawerPanel,
} from '@app/components/ui/drawer/Drawer';
import { Button } from 'mmds';
import useUI from '@app/components/ui/state';
import useDrawerDirection from '@app/lib/hooks/use-drawer-direction';
import { ReviewsResponseBody } from '@app/lib/reviewsio/methods/fetch-product-reviews';
import { SUPPORT_BASE_URL } from '../constants/support-paths';
import { useParams } from 'next/navigation';
import { Language } from '@app/lib/store-context/Context';
import Linker from '@app/components/linker/linker';

export type Labels = {
    id: string;
    label: string;
    hasData: boolean;
};

interface ProductAssistanceOverlayProps {
    product: any; // TODO
    warranty: ProductInformationDocumentData | null;
    shipping: ProductInformationDocumentData | null;
    sizeGuide: ProductInformationDocumentData | null;
    reviews: ReviewsResponseBody | null;
    labels: Labels[];
    shopifyProductSizing: string | null;
}

const ProductAssistanceDrawer = ({
    warranty,
    shipping,
    product,
    sizeGuide,
    reviews,
    labels,
    shopifyProductSizing,
}: ProductAssistanceOverlayProps) => {
    const [view, setView] = useState<string>('');
    const { lang } = useParams();
    const { openUIKey, productAssistanceView, toggleUI } = useUI();
    const isDrawerOpen = openUIKey === 'product-assistance';
    const drawerDirection = useDrawerDirection();

    useEffect(() => {
        if (productAssistanceView) {
            setView(productAssistanceView);
        }
    }, [productAssistanceView]);

    const handleNavItemClick = (item: string): void => {
        setView(`${item}`);
    };

    const toggleDrawer = () => {
        toggleUI(isDrawerOpen ? null : 'product-assistance');
    };

    return (
        <Drawer open={isDrawerOpen} onOpenChange={toggleDrawer}>
            <DrawerPanel inFrom={drawerDirection} variant="product-assistance">
                <DrawerHeader variant="product-assistance">
                    <ProductAssistanceDrawerHeader
                        navItems={labels}
                        onClick={handleNavItemClick}
                        activeItem={productAssistanceView as string}
                        drawerDirection={drawerDirection}
                    />
                </DrawerHeader>
                <ProductAssistanceDrawerContent
                    view={view}
                    warranty={warranty}
                    shipping={shipping}
                    product={product}
                    sizeGuide={sizeGuide}
                    reviews={reviews}
                    lang={lang as Language}
                    shopifyProductSizing={shopifyProductSizing}
                />
                <DrawerFooter>
                    <Linker
                        href={SUPPORT_BASE_URL}
                        title="Go to help center"
                        target="_blank"
                        lang={lang as Language}
                    >
                        <Button label="Help Center" icon={'northeast'} />
                    </Linker>
                </DrawerFooter>
            </DrawerPanel>
        </Drawer>
    );
};

export default ProductAssistanceDrawer;
